// src/lib/menage-journee-agent.js
//
// LES RÈGLES DE « MA JOURNÉE » — l'écran de l'agent. Helpers PURS.
//
// ⚠️ CET ÉCRAN SE TIENT D'UNE MAIN, DEVANT UNE PORTE. Tout ce qui n'aide pas à
// faire le ménage en sort. C'est le principe qui tranche chaque arbitrage
// ci-dessous : on ne dit pas POURQUOI une heure est celle-là, on dit l'heure ;
// on ne dit pas ce qu'un logement prend d'habitude, on dit ce qu'il prend
// aujourd'hui.
//
// AUCUN MONTANT n'entre ici ni n'en sort — un agent ne voit jamais ce que
// rapporte un logement.

import { dureeDuMenage, formaterDuree, formaterHeureCourte, minutesDeHeure } from './menage-frise'

const COMPLETED = 'completed'
const SKIPPED = 'skipped'

/**
 * LES TROIS COMPTEURS DU BANDEAU.
 *
 * ⚠️ « EN ATTENTE » A DISPARU. Un compteur qui affiche zéro tous les jours et
 * ne bouge jamais n'est pas une information, c'est un meuble : on cesse de le
 * lire, et on cesse en même temps de lire ses voisins. Le TEMPS DE TRAVAIL le
 * remplace — c'est ce qu'un agent veut vraiment savoir en ouvrant sa journée.
 *
 * `dureeDe(run)` rend les minutes — l'appelant y branche `dureeDuMenage`,
 * jamais un second calcul.
 */
export function compteursJournee(runs, dureeDe) {
  const duree = typeof dureeDe === 'function' ? dureeDe : () => null
  let aFaire = 0
  let termine = 0
  let minutes = 0
  for (const r of Array.isArray(runs) ? runs : []) {
    const statut = r?.status || 'scheduled'
    if (statut === SKIPPED) continue
    if (statut === COMPLETED) termine += 1
    else aFaire += 1
    const m = Number(duree(r))
    if (Number.isFinite(m) && m > 0) minutes += m
  }
  return { aFaire, termine, minutes, libelleMinutes: minutes > 0 ? formaterDuree(minutes) : '—' }
}

/**
 * « 1er ménage » / « 2e ménage » — la place de celui-ci dans la journée.
 *
 * Un agent qui enchaîne quatre logements a besoin de savoir OÙ il en est, pas
 * seulement ce qu'il a devant lui. L'ordre est celui de la liste affichée :
 * deux ordres différents entre le titre et la pile seraient pires que pas de
 * titre du tout.
 */
export function rangDuMenage(index) {
  // `Number(null)` vaut 0 : sans ce garde, une absence d'index produirait
  // « 1er ménage », c'est-à-dire une place inventée.
  if (index === null || index === undefined || index === '') return null
  const n = Number(index)
  if (!Number.isFinite(n) || n < 0) return null
  return n === 0 ? '1er ménage' : `${n + 1}e ménage`
}

/**
 * L'HEURE, EN HAUT À DROITE DE LA CARTE.
 *
 * Heure fixée → une seule heure, en grand : c'est une consigne, elle se lit de
 * loin. Pas d'heure fixée → la plage, plus petite : c'est un cadre, pas un
 * rendez-vous.
 *
 * @returns {{texte:string, grand:boolean}|null}
 */
export function heureEntete({ debutMin, plageDebut, plageFin }) {
  if (Number.isFinite(debutMin)) {
    return { texte: formaterHeureCourte(debutMin), grand: true }
  }
  const pd = minutesDeHeure(typeof plageDebut === 'string' ? plageDebut.slice(0, 5) : plageDebut)
  const pf = minutesDeHeure(typeof plageFin === 'string' ? plageFin.slice(0, 5) : plageFin)
  if (pd == null && pf == null) return null
  return {
    texte: `${pd != null ? formaterHeureCourte(pd) : '—'} → ${pf != null ? formaterHeureCourte(pf) : '—'}`,
    grand: false,
  }
}

/**
 * LA PHRASE DE L'ENCADRÉ HORAIRE.
 *
 * ⚠️ JAMAIS « VOUS CHOISISSEZ VOTRE HEURE ». Ce n'est pas une invitation à
 * décider : c'est une plage dans laquelle le travail doit tenir. La nuance
 * change ce qu'on fait à 16 h 30 sur une plage qui ferme à 17 h.
 *
 * ⚠️ ET JAMAIS LE POURQUOI. Ni « départ tardif accordé », ni « contrat 11:00 »,
 * ni « 4 h disponibles ». L'agent a besoin de l'heure ; la justification
 * appartient à celui qui a décidé.
 */
export function phraseHoraire({ debutMin, plageDebut, plageFin }) {
  const pd = minutesDeHeure(typeof plageDebut === 'string' ? plageDebut.slice(0, 5) : plageDebut)
  const pf = minutesDeHeure(typeof plageFin === 'string' ? plageFin.slice(0, 5) : plageFin)
  if (Number.isFinite(debutMin)) {
    return pf != null
      ? `Commencez à ${formaterHeureCourte(debutMin)}, terminez pour ${formaterHeureCourte(pf)}`
      : `Commencez à ${formaterHeureCourte(debutMin)}`
  }
  if (pd != null && pf != null) {
    return `À faire entre ${formaterHeureCourte(pd)} et ${formaterHeureCourte(pf)}`
  }
  return null
}

/**
 * LES VOYAGEURS QUI ARRIVENT — une information de TRAVAIL.
 *
 * ⚠️ CEUX QUI ARRIVENT, PAS CEUX QUI PARTENT. C'est le nombre qui décide
 * combien de serviettes, de draps et de capsules laisser. Servir le nombre de
 * partants ferait préparer le logement pour les gens de la veille.
 */
export function libelleArrivants(n) {
  const c = Number(n)
  if (!Number.isFinite(c) || c <= 0) return null
  return {
    principal: `${c} voyageur${c > 1 ? 's' : ''} arrive${c > 1 ? 'nt' : ''}`,
    secondaire: `linge pour ${c}`,
  }
}

/** La durée de CE ménage-là — règle unique, jamais recopiée. */
export function dureeDuRunAgent(run, menageDureeMin) {
  return dureeDuMenage(run, { menage_duree_min: menageDureeMin ?? null })
}

/**
 * LE COMPTE D'UN JOUR DE LA BANDE SEMAINE.
 *
 * ⚠️ UN TIRET, JAMAIS UNE CASE VIDE. Une case sans rien se lit comme une case
 * pas encore chargée — on attend que le chiffre arrive, et il n'arrive jamais.
 * Le tiret dit « journée libre », ce qui est une information.
 */
export function libelleJourSemaine(compte) {
  const n = Number(compte)
  return Number.isFinite(n) && n > 0 ? String(n) : '—'
}
